import { appVersion } from '../utils/appEnvironment'
import DOMPurify from 'dompurify'

export function odBaseUrl(): string {
  const baseUrl = process.env['REACT_APP_ONBOARDING_DIRECTOR_API_BASE_URL']
  return baseUrl
}

function sanitizeContent(input: unknown): unknown {
  if (typeof input === 'object' && !Array.isArray(input)) {
    for (const key in input) {
      input[key] = sanitizeContent(input[key])
    }
    return input
  }
  if (typeof input === 'string') {
    return DOMPurify.sanitize(input)
  }
  if (Array.isArray(input)) {
    return input.map((item) => sanitizeContent(item))
  }
  return input
}

export const isObjectEmpty = (obj: unknown) => !obj || !Object.keys(obj).length

/**
 * Creates a identifier string solely based on the current date.
 * @returns a string identifier
 */
const createDateIdentifier = () => {
  return Date.now().toString(36)
}

/**
 * A unique identifier to be user on the log prefix.
 */
export const loggerIdentifier = createDateIdentifier()

/* eslint-disable @typescript-eslint/no-explicit-any */
export class Logger {
  static logWithPrefix = <T>(severity: string, ...inputData: T[]) => {
    console[severity](
      `OOBECtrl - ${appVersion} - id: ${loggerIdentifier}`,
      sanitizeContent(inputData)
    )
  }

  static debug = <T extends any[]>(...inputData: T) =>
    Logger.logWithPrefix('debug', inputData)

  static log = <T extends any[]>(...inputData: T) =>
    Logger.logWithPrefix('log', inputData)

  static info = <T extends any[]>(...inputData: T) =>
    Logger.logWithPrefix('info', inputData)

  static warn = <T extends any[]>(...inputData: T) =>
    Logger.logWithPrefix('warn', inputData)

  static error = <T extends any[]>(...inputData: T) =>
    Logger.logWithPrefix('error', inputData)

  static logStringify = <T extends any[]>(
    severity: string,
    ...inputData: T
  ) => {
    console[severity](
      `OOBECtrl - ${appVersion} - id: ${loggerIdentifier}`,
      sanitizeContent(JSON.stringify(inputData, null, '\t'))
    )
  }
  static strDebug = <T extends any[]>(...inputData: T) =>
    Logger.logStringify('debug', inputData)
  static strLog = <T extends any[]>(...inputData: T) =>
    Logger.logStringify('log', inputData)
  static strInfo = <T extends any[]>(...inputData: T) =>
    Logger.logStringify('info', inputData)
  static strWarn = <T extends any[]>(...inputData: T) =>
    Logger.logStringify('warn', inputData)
  static strError = <T extends any[]>(...inputData: T) =>
    Logger.logStringify('error', inputData)
}

/**
 * Generates a simple hash code from a string.
 * @param str The input string to hash.
 * @returns A numeric hash as a string.
 */
export const simpleHash = (str: string): string => {
  let hash = 0
  if (str.length === 0) return hash.toString()
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert to 32bit integer
  }
  return hash.toString()
}
